import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import rpcSearch, { GlobalSearchResponse } from '../api/rpc/rpcSearch';
import { normalizeCompanySlug } from '../utils/company';

/**
 * Hook for global search across all entities
 * 
 * Features:
 * - Debounced input (handled by component)
 * - Cached results per query string
 * - 30-60s stale time
 * - Cancels previous requests when query changes
 * 
 * @param q - Search query string
 * @param company - Company slug (optional, auto-detected from URL)
 * @param limit - Results per entity type (default: 5)
 * @param types - Entity types to search (default: all)
 * @param options - Additional React Query options
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useGlobalSearch('john', company);
 * const clients = data?.grouped.clients || [];
 * ```
 */
export function useGlobalSearch(
  q: string,
  company?: string,
  limit: number = 5,
  types: Array<'clients' | 'leads' | 'tasks' | 'orders' | 'inventory' | 'users' | 'proposals'> = [
    'clients',
    'leads',
    'tasks',
    'orders',
    'inventory',
    'users',
    'proposals',
  ],
  options?: Omit<UseQueryOptions<GlobalSearchResponse>, 'queryKey' | 'queryFn'>
) {
  // Normalize company slug
  const normalizedCompany = company ? normalizeCompanySlug(company) : undefined;
  
  // Normalize query
  const normalizedQuery = String(q || '').trim();
  
  // Determine if search should be enabled
  // Minimum length: 2 chars (or 1 if numeric/phone)
  const isNumeric = /^\d+$/.test(normalizedQuery);
  const minLength = isNumeric ? 1 : 2;
  const enabled = !!normalizedCompany && normalizedQuery.length >= minLength;

  return useQuery<GlobalSearchResponse>({
    queryKey: ['globalSearch', normalizedCompany || '', normalizedQuery, limit, types],
    queryFn: async () => {
      if (!normalizedCompany) {
        throw new Error('Company is required');
      }
      
      if (!normalizedQuery || normalizedQuery.length < minLength) {
        return {
          ok: true,
          q: normalizedQuery,
          results: [],
          grouped: {
            clients: [],
            leads: [],
            tasks: [],
            orders: [],
            inventory: [],
            users: [],
            proposals: [],
          },
        };
      }

      return rpcSearch.globalSearch(normalizedQuery, limit, types);
    },
    enabled,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 60 * 1000, // 60 seconds (formerly cacheTime)
    placeholderData: (previousData) => previousData, // Show previous results while new ones load
    retry: 1, // Retry once on failure
    ...options,
  });
}

